import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/invoicing";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Globe2, Loader2, Package, Plus, Trash2 } from "lucide-react";

const newLine = () => ({ description: "", quantity: 1, unit_price: 0, gst: true, unit: "", item_id: null, _key: crypto.randomUUID() });

const F = ({ label, children, className = "" }) => (
  <div className={className}>
    <div className="text-xs font-medium text-zinc-500 mb-1">{label}</div>{children}
  </div>
);

const descRows = (text) =>
  Math.min(6, Math.max(1, Math.ceil((text || "").length / 55) + ((text || "").split("\n").length - 1)));

export default function InvoiceEditorPage({ recurring = false }) {
  const nav = useNavigate();
  const { invoiceId, recurringId } = useParams();
  const editingId = recurring ? recurringId : invoiceId;

  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!editingId);

  const [form, setForm] = useState({
    client_id: "", xero_tenant_id: "", bill_to_emails: "",
    issue_date: new Date().toISOString().slice(0, 10), due_date: "",
    reference: "", notes: "", push_without_payment: false,
    // recurring-only
    name: "", day_of_month: 1, due_days: 14, auto_send: false,
    auto_include_domain_renewals: false, active: true,
  });
  const [lines, setLines] = useState([newLine()]);
  const [catalogPick, setCatalogPick] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [c, i, t, s] = await Promise.all([
          api.get("/invoicing/clients"),
          api.get("/invoicing/items"),
          api.get("/integrations/xero/tenants").catch(() => ({ data: [] })),
          api.get("/invoicing/settings"),
        ]);
        setClients(c.data); setItems(i.data); setTenants(t.data || []);
        if (editingId) {
          const url = recurring
            ? `/invoicing/recurring/${editingId}`
            : `/invoicing/invoices/${editingId}`;
          const { data } = await api.get(url);
          setForm((f) => ({
            ...f, ...data,
            bill_to_emails: (data.bill_to_emails || []).join(", "),
            xero_tenant_id: data.xero_tenant_id || "",
          }));
          setLines((data.line_items || []).map((li) => ({ ...li, _key: crypto.randomUUID() })));
          setLoaded(true);
        } else if (s.data?.default_tenant_id) {
          setForm((f) => ({ ...f, xero_tenant_id: s.data.default_tenant_id }));
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load");
      }
    })();
  }, [editingId, recurring]);

  const totals = useMemo(() => {
    let subtotal = 0, gst = 0;
    lines.forEach((l) => {
      const amt = Number(l.quantity || 0) * Number(l.unit_price || 0);
      subtotal += amt;
      if (l.gst) gst += amt * 0.10;
    });
    return { subtotal, gst, total: subtotal + gst };
  }, [lines]);

  const setLine = (idx, patch) =>
    setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const pickClient = (client_id) => {
    const c = clients.find((x) => x.client_id === client_id);
    setForm((f) => ({
      ...f, client_id,
      bill_to_emails: f.bill_to_emails || (c?.emails || []).join(", "),
    }));
  };

  const addFromCatalogue = (item_id) => {
    const it = items.find((x) => x.item_id === item_id);
    if (!it) return;
    setLines((ls) => [...ls.filter((l) => l.description || l.unit_price), {
      description: it.description ? `${it.name} — ${it.description}` : it.name,
      quantity: 1, unit_price: it.unit_price, gst: it.gst, unit: it.unit,
      item_id: it.item_id, _key: crypto.randomUUID(),
    }]);
    setCatalogPick("");
  };

  const addClientDomains = async () => {
    if (!form.client_id) { toast.error("Pick a client first"); return; }
    try {
      const { data } = await api.get(`/invoicing/clients/${form.client_id}/domains`);
      const withCost = data.filter((d) => d.renewal_cost);
      if (!withCost.length) { toast.info("No domains with renewal costs for this client"); return; }
      setLines((ls) => [...ls.filter((l) => l.description || l.unit_price),
        ...withCost.map((d) => ({
          description: `Domain renewal — ${d.domain} (expires ${d.expiry_date})`,
          quantity: 1, unit_price: Number(d.renewal_cost), gst: true,
          unit: "year", item_id: null, _key: crypto.randomUUID(),
        }))]);
      toast.success(`Added ${withCost.length} domain(s)`);
    } catch (e) { toast.error("Failed to load client domains"); }
  };

  const save = async () => {
    if (!form.client_id) { toast.error("Select a client"); return; }
    const cleanLines = lines
      .filter((l) => l.description && Number(l.quantity) > 0)
      .map((l) => ({
        description: l.description, quantity: Number(l.quantity),
        unit_price: Number(l.unit_price), gst: !!l.gst,
        unit: l.unit || null, item_id: l.item_id || null,
      }));
    if (!cleanLines.length) { toast.error("Add at least one line item"); return; }
    const tenant = tenants.find((t) => t.tenant_id === form.xero_tenant_id);
    const common = {
      client_id: form.client_id,
      xero_tenant_id: form.xero_tenant_id || null,
      xero_tenant_name: tenant?.tenant_name || null,
      reference: form.reference || null, notes: form.notes || null,
      bill_to_emails: form.bill_to_emails.split(",").map((s) => s.trim()).filter(Boolean),
      line_items: cleanLines,
      push_without_payment: !!form.push_without_payment,
    };
    setSaving(true);
    try {
      if (recurring) {
        const body = {
          ...common, name: form.name || "Monthly invoice",
          day_of_month: Number(form.day_of_month), due_days: Number(form.due_days),
          auto_send: !!form.auto_send, active: !!form.active,
          auto_include_domain_renewals: !!form.auto_include_domain_renewals,
        };
        if (editingId) await api.put(`/invoicing/recurring/${editingId}`, body);
        else await api.post("/invoicing/recurring", body);
        toast.success("Recurring invoice saved");
        nav("/invoicing?tab=recurring");
      } else {
        const body = { ...common, issue_date: form.issue_date, due_date: form.due_date || null };
        const { data } = editingId
          ? await api.put(`/invoicing/invoices/${editingId}`, body)
          : await api.post("/invoicing/invoices", body);
        toast.success(`Invoice ${data.invoice_number} saved`);
        nav(`/invoicing/invoices/${data.invoice_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!loaded) return <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />;

  return (
    <div className="space-y-5 max-w-5xl" data-testid="invoice-editor-page">
      <div className="flex items-center gap-2">
        <Link to={recurring ? "/invoicing?tab=recurring" : "/invoicing"}>
          <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4" /></Button>
        </Link>
        <h1 className="text-2xl font-bold">
          {editingId ? "Edit" : "New"} {recurring ? "recurring invoice" : "invoice"}
        </h1>
      </div>

      <Card><CardContent className="p-5 space-y-4">
        {recurring && (
          <F label="Template name">
            <Input value={form.name} data-testid="recurring-name-input"
              placeholder="e.g. OSS — monthly package"
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </F>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          <F label="Client">
            <Select value={form.client_id} onValueChange={pickClient}>
              <SelectTrigger data-testid="invoice-client-select">
                <SelectValue placeholder="Select client…" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Issue from (Xero organisation)">
            <Select value={form.xero_tenant_id || "none"}
              onValueChange={(v) => setForm({ ...form, xero_tenant_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="invoice-tenant-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— default —</SelectItem>
                {tenants.map((t) => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
        </div>
        <F label="Send to (emails, comma separated)">
          <Input value={form.bill_to_emails} data-testid="invoice-emails-input"
            placeholder="accounts@client.com"
            onChange={(e) => setForm({ ...form, bill_to_emails: e.target.value })} />
        </F>
        <div className="grid md:grid-cols-3 gap-4">
          {recurring ? (
            <>
              <F label="Generate on day of month (1–28)">
                <Input type="number" min={1} max={28} value={form.day_of_month}
                  data-testid="recurring-day-input"
                  onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} />
              </F>
              <F label="Due days after issue">
                <Input type="number" min={0} value={form.due_days}
                  onChange={(e) => setForm({ ...form, due_days: e.target.value })} />
              </F>
            </>
          ) : (
            <>
              <F label="Issue date">
                <Input type="date" value={form.issue_date} data-testid="invoice-issue-date"
                  onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
              </F>
              <F label="Due date (blank = default terms)">
                <Input type="date" value={form.due_date || ""} data-testid="invoice-due-date"
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                <div className="flex gap-1 mt-1">
                  {[7, 14, 30].map((d) => (
                    <Button key={d} type="button" variant="outline" size="sm"
                      className="h-6 px-2 text-xs" data-testid={`terms-${d}-btn`}
                      onClick={() => {
                        const base = new Date(form.issue_date || new Date());
                        base.setDate(base.getDate() + d);
                        setForm({ ...form, due_date: base.toISOString().slice(0, 10) });
                      }}>
                      {d} days
                    </Button>
                  ))}
                </div>
              </F>
            </>
          )}
          <F label="Reference">
            <Input value={form.reference || ""}
              onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          </F>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-semibold text-sm flex-1">Line items (prices ex GST)</div>
          <Select value={catalogPick} onValueChange={addFromCatalogue}>
            <SelectTrigger className="w-64" data-testid="add-catalogue-select">
              <Package className="h-4 w-4 mr-1 text-zinc-400" />
              <SelectValue placeholder="Add from catalogue…" />
            </SelectTrigger>
            <SelectContent>
              {items.map((it) => (
                <SelectItem key={it.item_id} value={it.item_id}>
                  {it.name} · {fmtMoney(it.unit_price)}/{it.unit}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={addClientDomains}
            data-testid="add-domains-btn">
            <Globe2 className="h-4 w-4 mr-1" /> Add client domains
          </Button>
          <Button variant="outline" size="sm" data-testid="add-blank-line-btn"
            onClick={() => setLines([...lines, newLine()])}>
            <Plus className="h-4 w-4 mr-1" /> Blank line
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b">
              <th className="p-2 w-[45%]">Description</th>
              <th className="p-2">Qty</th><th className="p-2">Unit price</th>
              <th className="p-2 text-center">GST 10%</th>
              <th className="p-2 text-right">Amount</th><th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l._key || i} className="border-b align-top">
                <td className="p-2">
                  <Textarea value={l.description} data-testid={`line-desc-${i}`}
                    placeholder="Description" rows={descRows(l.description)}
                    className="min-h-0 resize-none"
                    onChange={(e) => setLine(i, { description: e.target.value })} />
                </td>
                <td className="p-2 w-24">
                  <Input type="number" step="0.01" value={l.quantity}
                    data-testid={`line-qty-${i}`}
                    onChange={(e) => setLine(i, { quantity: e.target.value })} />
                </td>
                <td className="p-2 w-32">
                  <Input type="number" step="0.01" value={l.unit_price}
                    data-testid={`line-price-${i}`}
                    onChange={(e) => setLine(i, { unit_price: e.target.value })} />
                </td>
                <td className="p-2 text-center">
                  <Switch checked={!!l.gst} data-testid={`line-gst-${i}`}
                    onCheckedChange={(v) => setLine(i, { gst: v })} />
                </td>
                <td className="p-2 text-right whitespace-nowrap pt-4">
                  {fmtMoney(Number(l.quantity || 0) * Number(l.unit_price || 0))}
                </td>
                <td className="p-2">
                  <Button variant="ghost" size="sm"
                    onClick={() => setLines(lines.filter((_, x) => x !== i))}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end">
          <div className="w-72 space-y-1 text-sm" data-testid="invoice-totals">
            <div className="flex justify-between"><span className="text-zinc-500">Subtotal (ex GST)</span><span>{fmtMoney(totals.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">GST 10%</span><span>{fmtMoney(totals.gst)}</span></div>
            <div className="flex justify-between font-bold text-base border-t pt-1">
              <span>Total (inc GST)</span><span>{fmtMoney(totals.total)}</span>
            </div>
          </div>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-5 space-y-3">
        <F label="Notes (shown on the invoice)">
          <Textarea rows={2} value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </F>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={!!form.push_without_payment}
            data-testid="push-without-payment-toggle"
            onCheckedChange={(v) => setForm({ ...form, push_without_payment: v })} />
          <span>
            <b>Send to Xero without waiting for payment</b>
            <span className="text-zinc-500"> — pushes the invoice to Xero as soon as it's sent,
            instead of the default (push once fully paid).</span>
          </span>
        </label>
        {recurring && (
          <>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!form.auto_send} data-testid="recurring-autosend-toggle"
                onCheckedChange={(v) => setForm({ ...form, auto_send: v })} />
              <span><b>Auto-send</b><span className="text-zinc-500"> — email the invoice to
              the client automatically when generated (otherwise it lands as a draft for review).</span></span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!form.auto_include_domain_renewals}
                data-testid="recurring-domains-toggle"
                onCheckedChange={(v) => setForm({ ...form, auto_include_domain_renewals: v })} />
              <span><b>Auto-add domain renewals</b><span className="text-zinc-500"> — each run,
              add the client's domains expiring in the next month at their TPP renewal cost.</span></span>
            </label>
          </>
        )}
      </CardContent></Card>

      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} data-testid="invoice-save-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
          {editingId ? "Save changes" : recurring ? "Create recurring invoice" : "Create draft invoice"}
        </Button>
      </div>
    </div>
  );
}
